/**
 * The book's figures for accounts whose source is TallyPrime, read the
 * company's way (docs/tally/18 §1.3 — decision D3, resolved). Every screen of
 * the product reads these, so they are worked out once, here, from
 * TallyPrime's own bills:
 *
 *  - `ageing.windows`: the open debit bills in the company's own windows, as
 *    TallyPrime's Age-wise report shows them, with what is not yet due, not in
 *    any bucket and the credits beside them, never inside — what every screen
 *    that shows ageing shows (the customer, Today, Reports, the Customers list
 *    and export, the AI summary);
 *  - the rules' figures — the four standard 45-day bands, over 90 and over
 *    45 — which decide an account's automatic "Late", the "Older than"
 *    filters and the order by age. How they are counted depends on whether the
 *    company has chosen how its receivables are aged (`BookRules`).
 *
 * The sync and a settings change both write them, each in one transaction,
 * so no screen can show one company's ageing two ways.
 */
import { AGEING_BUCKETS, type AgeingAmounts, type AgeingBucket, type AgeingTypes, type AgeingWindows, type BalanceType } from './customer.js';
import { STANDARD_BOUNDARIES, bucketLabels } from './ageingEngine.js';
import { overdueAgeing } from './money.js';
import { fromPaise, toPaise, type SourceAccountFact, type SourceBillFact, type SourceFacts } from './receivablesFacts.js';
import { interpretReceivables } from './receivablesPosition.js';
import type { ReceivablesSettings } from './receivablesSettings.js';

/**
 * How the rules' figures are counted (docs/tally/18 §1.3):
 *
 *  - `legacy` — for a company that has not chosen how its receivables are
 *    aged: exactly as the TallyPrime source wrote them when it was released
 *    (0034 and 0035). Every open bill by **bill date**, a credit note or an
 *    advance netted into the band of its own date (a band that nets to
 *    credit is stored as such), over 90 the two oldest bands netted, and
 *    over 45 from the bills' **due dates**, netted. So an upgrade moves no
 *    account between overdue and not overdue.
 *  - `basis` — once the company has chosen: the open debit bills by its
 *    basis, credits beside the bands, never netted into one; over 90 and over
 *    45 are those bands' own roll-ups.
 *
 * Either way, every screen shows the company's windows (`ageing.windows`).
 */
export type BookRules = 'legacy' | 'basis';

/** A saved version of the settings is the company's choice; version 0 (none saved) is "not chosen yet". */
export const rulesFor = (settingsVersion: number): BookRules => (settingsVersion > 0 ? 'basis' : 'legacy');

export interface BookFigures {
    ageing: AgeingAmounts & { windows: AgeingWindows };
    ageingTypes: AgeingTypes;
    over90: number;
    over90Type: BalanceType;
    dueOver45: number;
    dueOver45Type: BalanceType;
}

const DAY = 86_400_000;
const daysBetween = (from: string, to: string): number => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY);
/** The released bands: a bill of any age up to 45 days, including one dated after the as-of date, is in the first. */
const releasedBand = (ageDays: number): AgeingBucket => (ageDays <= 45 ? '1-45' : ageDays <= 90 ? '46-90' : ageDays <= 135 ? '91-135' : '>135');

/**
 * One account's rules' figures as released (`legacy`), from its open bills
 * (signed, positive = Dr) as of a date. In paise, so nothing depends on the
 * order the bills arrive in.
 */
export function legacyRuleFigures(bills: readonly Pick<SourceBillFact, 'billDate' | 'dueDate' | 'pending'>[], asOf: string): Omit<BookFigures, 'ageing'> & { ageing: AgeingAmounts } {
    const bands: Record<AgeingBucket, number> = { '1-45': 0, '46-90': 0, '91-135': 0, '>135': 0 };
    let over45 = 0;
    for (const b of bills) {
        const p = toPaise(b.pending);
        bands[releasedBand(daysBetween(b.billDate, asOf))] += p;
        if (daysBetween(b.dueDate ?? b.billDate, asOf) > 45) over45 += p;
    }
    const ageing = { '1-45': 0, '46-90': 0, '91-135': 0, '>135': 0 } as AgeingAmounts;
    const ageingTypes: AgeingTypes = {};
    for (const k of AGEING_BUCKETS) {
        ageing[k] = fromPaise(Math.abs(bands[k]));
        if (bands[k] < 0) ageingTypes[k] = 'Cr';
    }
    const over90 = bands['91-135'] + bands['>135'];
    return {
        ageing, ageingTypes,
        over90: fromPaise(Math.abs(over90)), over90Type: over90 < 0 ? 'Cr' : 'Dr',
        dueOver45: fromPaise(Math.abs(over45)), dueOver45Type: over45 < 0 ? 'Cr' : 'Dr',
    };
}

/** Each account's book figures, keyed by its source key. */
export function bookFiguresFromFacts(facts: Pick<SourceFacts, 'asOf' | 'accounts' | 'unmatchedBills'>, settings: ReceivablesSettings, rules: BookRules): Map<string, BookFigures> {
    const own = interpretReceivables(facts, settings);
    const standard = rules === 'basis' ? interpretReceivables(facts, { ...settings, ageing: { ...settings.ageing, boundaries: [...STANDARD_BOUNDARIES] } }) : null;
    const bands = new Map((standard?.accounts ?? []).map(a => [a.sourceKey, a.buckets]));
    const billsOf = new Map(facts.accounts.map(a => [a.sourceKey, a.bills]));
    const out = new Map<string, BookFigures>();
    for (const a of own.accounts) {
        const unbilledP = toPaise(a.unbilled);
        const windows: AgeingWindows = {
            basis: settings.ageing.basis,
            boundaries: [...settings.ageing.boundaries],
            amounts: [...a.buckets],
            notYetDue: a.notYetDue,
            notAged: fromPaise(toPaise(a.unagedNoDueDate) + Math.max(0, unbilledP)),
            credits: fromPaise(toPaise(a.creditBills) + Math.max(0, -unbilledP)),
        };
        if (rules === 'legacy') {
            const released = legacyRuleFigures(billsOf.get(a.sourceKey) ?? [], facts.asOf);
            out.set(a.sourceKey, { ...released, ageing: { ...released.ageing, windows } });
            continue;
        }
        const [b1, b2, b3, b4] = (bands.get(a.sourceKey) ?? [0, 0, 0, 0]).map(toPaise) as [number, number, number, number];
        out.set(a.sourceKey, {
            ageing: { '1-45': fromPaise(b1), '46-90': fromPaise(b2), '91-135': fromPaise(b3), '>135': fromPaise(b4), windows },
            ageingTypes: {},
            over90: fromPaise(b3 + b4),
            over90Type: 'Dr',
            dueOver45: fromPaise(b2 + b3 + b4),
            dueOver45Type: 'Dr',
        });
    }
    return out;
}

/**
 * What an account's windows add up to: the windows, what is not yet due and
 * what is in no bucket, less the credits beside them — its balance in
 * TallyPrime, signed (positive = Dr). Credits are never in a window, so
 * nothing is counted twice and nothing disappears.
 */
export const windowsBalance = (w: Pick<AgeingWindows, 'amounts' | 'notYetDue' | 'notAged' | 'credits'>): number =>
    fromPaise(w.amounts.reduce((sum, x) => sum + toPaise(x), 0) + toPaise(w.notYetDue) + toPaise(w.notAged) - toPaise(w.credits));

/** How Timely reads a company's receivables: its settings, and whether it has chosen them. */
export interface CompanyReading { settings: ReceivablesSettings; rules: BookRules }

export interface AgeingImpact {
    /** TallyPrime accounts compared. */
    accounts: number;
    becomingOverdue: { sourceKey: string; name: string }[];
    noLongerOverdue: { sourceKey: string; name: string }[];
    /** The company's windows now, and as they would be: label and amount. */
    before: { label: string; amount: number }[];
    after: { label: string; amount: number }[];
}

/**
 * What saving a change would do to collection work, before it is saved
 * (docs/tally/18 §10): which accounts would start or stop counting as
 * overdue — money over 45 days by the rules, which makes an account "Late"
 * by itself and puts it in the "Older than" filters — and how the money
 * moves between the windows. Overdue is read exactly as every screen reads
 * it (`overdueAgeing`): an account in credit owes nothing.
 */
export function ageingChangeImpact(facts: Pick<SourceFacts, 'asOf' | 'accounts' | 'unmatchedBills'>, before: CompanyReading, after: CompanyReading): AgeingImpact {
    const was = bookFiguresFromFacts(facts, before.settings, before.rules);
    const will = bookFiguresFromFacts(facts, after.settings, after.rules);
    const overdue = (a: SourceAccountFact, f: BookFigures | undefined): boolean => {
        if (!f) return false;
        const o = overdueAgeing({
            total: Math.abs(a.closing), totalType: a.closing < 0 ? 'Cr' : 'Dr', ageing: f.ageing, ageingTypes: f.ageingTypes,
            over90: f.over90, over90Type: f.over90Type, dueOver45: f.dueOver45, dueOver45Type: f.dueOver45Type,
        });
        return o.over45 > 0 || o.over90 > 0;
    };
    const becomingOverdue: AgeingImpact['becomingOverdue'] = [];
    const noLongerOverdue: AgeingImpact['noLongerOverdue'] = [];
    let accounts = 0;
    for (const a of facts.accounts) {
        if (!was.has(a.sourceKey) && !will.has(a.sourceKey)) continue;
        accounts++;
        const from = overdue(a, was.get(a.sourceKey)), to = overdue(a, will.get(a.sourceKey));
        if (!from && to) becomingOverdue.push({ sourceKey: a.sourceKey, name: a.name });
        if (from && !to) noLongerOverdue.push({ sourceKey: a.sourceKey, name: a.name });
    }
    const totals = (figures: Map<string, BookFigures>, settings: ReceivablesSettings) => {
        const labels = bucketLabels(settings.ageing);
        const sums = labels.map(() => 0);
        for (const f of figures.values()) f.ageing.windows.amounts.forEach((x, i) => { sums[i] = (sums[i] ?? 0) + toPaise(x); });
        return labels.map((label, i) => ({ label, amount: fromPaise(sums[i] ?? 0) }));
    };
    return { accounts, becomingOverdue, noLongerOverdue, before: totals(was, before.settings), after: totals(will, after.settings) };
}

const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * The windows stored with an account, read back defensively (they come from
 * the database as JSON): null unless every part has the shape written above.
 */
export function readAgeingWindows(ageing: unknown): AgeingWindows | null {
    if (typeof ageing !== 'object' || ageing === null) return null;
    const w = (ageing as { windows?: unknown }).windows;
    if (typeof w !== 'object' || w === null) return null;
    const { basis, boundaries, amounts, notYetDue, notAged, credits } = w as Record<string, unknown>;
    if (basis !== 'bill_date' && basis !== 'due_date') return null;
    if (!Array.isArray(boundaries) || !boundaries.every(b => Number.isInteger(b) && (b as number) > 0)) return null;
    if (boundaries.some((b, i) => i > 0 && (b as number) <= (boundaries[i - 1] as number))) return null;
    if (!Array.isArray(amounts) || amounts.length !== boundaries.length + 1 || !amounts.every(isNumber)) return null;
    if (!isNumber(notYetDue) || !isNumber(notAged) || !isNumber(credits)) return null;
    return { basis, boundaries: boundaries as number[], amounts: amounts as number[], notYetDue, notAged, credits };
}

/** The windows with their labels, in order — as Settings names them. */
export function ageingWindowRows(w: AgeingWindows): { label: string; amount: number }[] {
    const labels = bucketLabels({ basis: w.basis, boundaries: w.boundaries });
    return w.amounts.map((amount, i) => ({ label: labels[i] ?? '', amount }));
}

/** Whether two accounts' figures say the same thing (the planner's "nothing new"), windows included. */
export function sameWindows(a: AgeingWindows | null | undefined, b: AgeingWindows | null | undefined): boolean {
    if (!a || !b) return !a && !b;
    const money = (n: number) => toPaise(n);
    return a.basis === b.basis
        && a.boundaries.length === b.boundaries.length && a.boundaries.every((x, i) => x === b.boundaries[i])
        && a.amounts.length === b.amounts.length && a.amounts.every((x, i) => money(x) === money(b.amounts[i]!))
        && money(a.notYetDue) === money(b.notYetDue) && money(a.notAged) === money(b.notAged) && money(a.credits) === money(b.credits);
}

/** The four standard bands of an ageing object, without anything stored beside them. */
export const standardBands = (ageing: AgeingAmounts): number[] => AGEING_BUCKETS.map(b => Number(ageing[b]) || 0);
